import {round} from 'lodash-es';
import {SaltusAny} from './SaltusAny';

const ABBREVIATION_LIST = ['', 'k', 'M', 'B', 'T'];

export const parseCurrencyString = (value: string, extraValue = '') =>
  parseFloat(value.replaceAll(',', '').replaceAll('£', '').replaceAll(extraValue, ''));

const formatCurrency = (numberValue: number, decimalPlaces: number) =>
  numberValue.toLocaleString('en-gb', {minimumFractionDigits: decimalPlaces});

const rounder = (value: number, exp: number) =>
  (100 * Math.round(value / 10 ** exp)) / 100;

/**
 * takes a value like '1k' and returns 1000
 * @param {string} value
 * @returns {number}
 */
export const expandNumber = (value: string): number => {
  const multiplierStr = value.charAt(value.length - 1);
  const multiplierIndex = ABBREVIATION_LIST.indexOf(multiplierStr);
  if (multiplierIndex === -1) {
    return parseCurrencyString(value);
  }
  const multiplier = 10 ** (multiplierIndex * 3);
  return parseCurrencyString(value, multiplierStr) * multiplier;
};

/**
 * takes in a number or string and returns a parsed number
 * @param  value
 * @returns {number} parsed number value or 0 if incorrect type passed in
 */
export const getCurrencyFloat = (value: SaltusAny): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsedValue = expandNumber(value);
    if (Number.isNaN(parsedValue)) {
      return 0;
    }
    return parsedValue;
  }

  return 0;
};

/**
 * converts trailing zeroes into letters, e.g. 1,000 -> 1k
 * @param {number} value - must be a number
 * @returns {string} an abbreviated string
 */
export const abbreviateNumber = (value: number): string => {
  const log10Value = parseFloat(Math.log10(value).toFixed(1));
  const logIndex = Math.min(Math.floor(log10Value / 3), ABBREVIATION_LIST.length - 1);
  const mainNumber = formatCurrency(rounder(value, logIndex * 3), 0);
  return `${mainNumber}${ABBREVIATION_LIST[logIndex]}`;
};

interface getCurrencyStringOptions {
  hasPoundSign?: boolean;
  decimalPlaces?: number;
  isAbbreviated?: boolean;
}

/**
 * returns a formatted currency string, e.g. £10.00
 * @param {number} value should be a string or number
 * @param {getCurrencyStringOptions} options has the following fields: hasPoundSign, decimalPlaces and isAbbreviated
 * @returns {string} formatted currency string
 */
export const getCurrencyString = (
  value: number | string,
  options: getCurrencyStringOptions = {}
): string => {
  const {hasPoundSign = true, decimalPlaces = 2, isAbbreviated = false} = options;

  if (typeof value !== 'number' && typeof value !== 'string') {
    return '';
  }
  const poundSign = hasPoundSign ? '£' : '';
  const numberValue = typeof value === 'number' ? value : getCurrencyFloat(value);
  if (typeof numberValue !== 'number' || Number.isNaN(numberValue)) {
    return '';
  }
  const formattedNumber = isAbbreviated
    ? abbreviateNumber(numberValue)
    : formatCurrency(numberValue, decimalPlaces);

  return `${poundSign}${formattedNumber}`;
};

export const getPercentValidation = (val: unknown, decimalPlaces = 2) => {
  if (Number.isNaN(val)) {
    return 'Not a valid percentage';
  }
  const valNum = Number(val);
  const split = valNum.toString().split('.');
  const hasMoreThanXDP = split.length > 1 && split[1].length > decimalPlaces;

  if (hasMoreThanXDP || round(valNum, decimalPlaces) !== valNum) {
    return 'Not allowed more than 2 decimal places';
  }
  return null;
};

type numberValidationOptions = {
  decimalPlaces: number | false;
  allowZero?: boolean;
  allowNegative?: boolean;
};
export const getNumberValidation = (
  val: SaltusAny,
  {decimalPlaces, allowNegative, allowZero = true}: numberValidationOptions
) => {
  const valNum = Number(val);
  if (Number.isNaN(valNum)) {
    return 'Not a valid number';
  }
  if (!allowNegative && valNum < 0) {
    return 'Negative value not allowed';
  }
  if (!allowZero && valNum === 0) {
    return 'Zero not allowed';
  }
  if (decimalPlaces !== false) {
    const checkOne = round(valNum, decimalPlaces) !== valNum;
    const split = val.toString().split('.');
    const hasMoreThanXDP = split.length > 1 && split[1].length > decimalPlaces;
    if (hasMoreThanXDP || checkOne) {
      if (decimalPlaces === -1) {
        return null;
      }
      if (decimalPlaces === 0) {
        return 'Not allowed decimal places';
      }

      return `Not allowed more than ${decimalPlaces} decimal places`;
    }
  }
  return null;
};
